import axiosInstance from '@/services/api';
import axios from 'axios';
import CryptoJS from 'crypto-js';
import {
	Alert,
	Button,
	Card,
	Checkbox,
	Label,
	Spinner,
	TextInput,
} from 'flowbite-react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import {
	FaEye,
	FaEyeSlash,
	FaLock,
	FaUser,
} from 'react-icons/fa';
import logoHiGuard from '../../../assets/images/HI-GUARD 2.png';
import logoKjri from '../../../assets/images/logo-kjri-jb.png';

import {
	getEntityBySecret,
	getHardcodeList,
} from '@/services/encrypt/entitySecretService';
import { setEntity, setToken, setUser, setPasswordStatus, setCustomerId } from '../../../utils/handleToken';

const API_URL = '/mobile/login';

export default function Login() {
	const [userId, setUserIdState] = useState('');
	const [password, setPassword] = useState('');
	const [error, setError] = useState('');
	const [redirectMessage, setRedirectMessage] = useState('');
	const [showPassword, setShowPassword] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const router = useRouter();
	const [entityData, setEntityData] = useState<any>(null);
	const [logoUrl, setLogoUrl] = useState<string>('');
	const [defaultLogo, setDefaultLogo] = useState(logoHiGuard);
	const [appName, setAppName] = useState('HI-Guard');

	// Tambahkan useEffect baru untuk menentukan logo default
	useEffect(() => {
		const hostname = window?.location?.hostname;
		if (hostname === 'kjrijb.my') {
			setDefaultLogo(logoKjri);
		} else {
			setDefaultLogo(logoHiGuard);
		}
	}, []);

	const handleLogin = async (e: React.FormEvent) => {
		e.preventDefault();
		setError('');
		setRedirectMessage('');
		setIsLoading(true);

		try {
			const response = await axiosInstance.post(
				API_URL,
				{
					user_id: userId,
					password: password,
				},
				{
					headers: {
						req_from: 'website',
						// req_from: 'apps',
						type_password: 'd',
					},
				}
			);

			// console.log(response.data.data.user.user_id);

			if (response.data.success) {
				setToken(response.data.data.token);
				setUser(response.data.data.user.name);
				setEntity(response.data.data.user.entity_id);
				setCustomerId(response.data.data.user.customer_id);

				// Store password status for security enforcement
				const passwordStatus = response.data.data.user.status_password;
				setPasswordStatus(passwordStatus);

				// Check if password status is 'd' and redirect to change password
				if (passwordStatus === 'd') {
					setRedirectMessage('Anda akan diarahkan ke halaman ganti password untuk keamanan akun Anda...');
					setTimeout(() => {
						router.push('/auth/change-password?from=login');
					}, 1500);
					return;
				}

				// Penambahan logika pengalihan berdasarkan user_id
				if (response.data.data.user.id === 105) {
					window.location.href = '/bo-dasbor-ijb';
				} else {
					window.location.href = '/hra-dasbor-ent';
				}
			} else {
				setError('Login gagal. Silakan coba lagi.');
			}
		} catch (error: any) {
			if (axios.isAxiosError(error) && error.response) {
				setError(error.response.data.message || 'Terjadi kesalahan saat login');
			} else {
				setError('Terjadi kesalahan saat menghubungi server');
			}
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		const fetchEntityLogo = async () => {
			console.time('fetchEntityLogo');
			try {
				const currentHost = window.location.hostname;
				console.log('[Login] Current hostname:', currentHost);

				const hardcodeResponse = await getHardcodeList();
				const matchingConfig = hardcodeResponse.data.find((config) =>
					currentHost.includes(config.gh_config_name)
				);

				console.log('[Login] Matching config:', matchingConfig);

				if (matchingConfig) {
					// Set app name dari config
					setAppName(matchingConfig.gh_config_data || 'Hi-Guard');

					try {
						const entityResponse = await getEntityBySecret(
							matchingConfig.gh_config_code
						);
						console.log('[Login] Entity response:', entityResponse);

						// Set entity data untuk digunakan di welcome message
						setEntityData(entityResponse);

						// Menggunakan URL logo langsung dari response API
						const logoUrl =
							entityResponse.entity?.me_logo_med ||
							entityResponse.entity?.me_logo_big ||
							entityResponse.entity?.me_logo_small;

						if (logoUrl) {
							console.log('[Login] Setting logo URL:', logoUrl);
							setLogoUrl(logoUrl);
						} else {
							console.log('[Login] No logo found, using default');
							setLogoUrl('');
						}
					} catch (entityError) {
						console.error('[Login] Error fetching entity:', entityError);
						setLogoUrl('');
					}
				} else {
					console.warn(
						'[Login] No matching config found for hostname:',
						currentHost
					);
					setLogoUrl('');
				}
			} catch (error) {
				console.error('[Login] Error in fetchEntityLogo:', error);
				setLogoUrl('');
			} finally {
				console.timeEnd('fetchEntityLogo');
			}
		};

		fetchEntityLogo();
	}, []);

	useEffect(() => {
		const host = window.location.host;
		if (host.includes('hi-guard.id') || host === 'localhost:3001') {
			// style theme logic reserved for future use
		}
	}, []);

	// Fungsi untuk mengenkripsi entity_id
	const encryptData = (data: { entity_id: string }) => {
		const encryptedData = CryptoJS.AES.encrypt(
			JSON.stringify(data),
			process.env.NEXT_PUBLIC_NAME_ENTITY_SECRET || ''
		).toString();
		return encryptedData;
	};

	return (
		<div className="min-h-screen flex items-center justify-center px-4">
			<Card className="w-full max-w-6xl overflow-hidden rounded-3xl">
				<div className="flex flex-col lg:flex-row">
					{/* Left side - Login Form */}
					<div className="w-full lg:w-7/12 p-8 lg:p-12">
						<div className="max-w-xl mx-auto">
							<div className="text-center mb-5">
								{logoUrl ? (
									<Image
										src={logoUrl}
										alt="Entity Logo"
										width={200}
										height={200}
										className="mx-auto mb-6"
										unoptimized
										onError={() => {
											console.log(
												'[Login] Error loading logo, falling back to default'
											);
											setLogoUrl('');
										}}
									/>
								) : (
									<Image
										src={defaultLogo}
										alt="Default Logo"
										width={200}
										height={200}
										className="mx-auto mb-6"
									/>
								)}
								<h1 className="text-2xl font-bold text-gray-900 dark:text-white">
									Masuk ke akun Anda
								</h1>
							</div>

							<form onSubmit={handleLogin} className="flex flex-col gap-3">
								<div>
									<div className="mb-2 block">
										<Label
											htmlFor="user-id"
											value="User ID"
											className="text-base"
										/>
									</div>
									<TextInput
										id="user-id"
										icon={FaUser}
										placeholder="Masukkan User ID Anda"
										value={userId}
										onChange={(e) => setUserIdState(e.target.value)}
										required
										sizing="md"
									/>
								</div>

								<div>
									<div className="mb-2 block">
										<Label
											htmlFor="password"
											value="Password"
											className="text-base"
										/>
									</div>
									<div className="relative">
										<TextInput
											id="password"
											type={showPassword ? 'text' : 'password'}
											icon={FaLock}
											placeholder="Masukkan Password Anda"
											value={password}
											onChange={(e) => setPassword(e.target.value)}
											required
											sizing="md"
										/>
										<button
											type="button"
											onClick={() => setShowPassword(!showPassword)}
											className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
											tabIndex={-1}
										>
											{showPassword ? <FaEyeSlash className="w-4 h-4" /> : <FaEye className="w-4 h-4" />}
										</button>
									</div>
								</div>

								{error && (
									<Alert color="failure" className="mt-2">
										{error}
									</Alert>
								)}

								{redirectMessage && (
									<Alert color="info" className="mt-2">
										<div className="flex items-center gap-2">
											<Spinner size="sm" />
											<span>{redirectMessage}</span>
										</div>
									</Alert>
								)}

								<div className="flex items-center">
									<div className="flex items-center gap-2">
										<Checkbox id="remember" />
										<Label htmlFor="remember" className="text-base">
											Ingat Saya
										</Label>
									</div>
								</div>

								<Button
									type="submit"
									gradientDuoTone="purpleToBlue"
									disabled={isLoading}
									className="w-full"
									size="lg"
								>
									{isLoading ? (
										<>
											<Spinner size="sm" light className="mr-2" />
											Memproses...
										</>
									) : (
										'Masuk'
									)}
								</Button>
							</form>
						</div>
					</div>

					{/* Right side - Welcome Banner */}
				<div className="w-full lg:w-4/6 hidden lg:flex bg-gradient-to-br from-blue-600 to-blue-800 items-center justify-center lg:-mr-6 lg:-my-6 rounded-tl-[30px] rounded-tr-[180px] rounded-br-[30px] rounded-bl-[180px] py-16 lg:py-0 border-x-8 border-x-slate-200">
					<div className="text-center text-white px-8 lg:px-12">
						<p className="text-lg lg:text-xl mb-4 opacity-90">
							Selamat datang,
						</p>
						<h2 className="text-3xl lg:text-4xl font-bold mb-6">
							{entityData?.entity?.me_entity_name
								? entityData.entity.me_entity_name
								: 'HI-Guard'}
						</h2>
						<p className="text-lg lg:text-xl">
							{appName} Dashboard
						</p>
					</div>
				</div>
				</div>
			</Card>
		</div>
	);
}
